import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  MIN_ASSIGNABLE_USER_QUERY_LENGTH,
  type AssignableUser,
} from '../../api/projectMembers';
import { ASSIGNABLE_PROJECT_ROLES } from '../../roles';
import { classifyMemberError, MemberErrorKind } from './memberErrors';
import { ProjectRole } from './types';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_ROLE: ProjectRole = 'Collaborator';

const useStyles = makeStyles(theme => ({
  caution: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
  },
  secondary: {
    color: theme.palette.text.secondary,
  },
}));

type AddMemberDialogProps = {
  open: boolean;
  searchUsers: (
    query: string,
    signal?: AbortSignal,
  ) => Promise<AssignableUser[]>;
  onAdd: (user: AssignableUser, role: ProjectRole) => Promise<void>;
  onClose: () => void;
};

export function AddMemberDialog({
  open,
  searchUsers,
  onAdd,
  onClose,
}: AddMemberDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<AssignableUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<AssignableUser | null>(null);
  const [role, setRole] = useState<ProjectRole>(DEFAULT_ROLE);
  const [submitting, setSubmitting] = useState(false);
  const [errorKind, setErrorKind] = useState<MemberErrorKind>();

  useEffect(() => {
    if (open) return;
    setQuery('');
    setOptions([]);
    setSelected(null);
    setRole(DEFAULT_ROLE);
    setErrorKind(undefined);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (query.trim().length < MIN_ASSIGNABLE_USER_QUERY_LENGTH) {
      setOptions([]);
      setSearching(false);
      return undefined;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        setOptions(await searchUsers(query, controller.signal));
        setErrorKind(undefined);
      } catch (err) {
        if (!controller.signal.aborted) {
          setOptions([]);
          setErrorKind(classifyMemberError(err));
        }
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, searchUsers]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setErrorKind(undefined);
    try {
      await onAdd(selected, role);
      onClose();
    } catch (err) {
      setErrorKind(classifyMemberError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={() => {
        if (!submitting) onClose();
      }}
    >
      <DialogTitle>{t('members.addTitle')}</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={options}
          loading={searching}
          value={selected}
          filterOptions={candidates => candidates}
          getOptionLabel={user => user.name || user.email || user.id}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(_event, user) => setSelected(user)}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') setQuery(value);
          }}
          loadingText={t('members.searching')}
          noOptionsText={
            query.trim().length < MIN_ASSIGNABLE_USER_QUERY_LENGTH
              ? t('members.searchMinLength', {
                  count: MIN_ASSIGNABLE_USER_QUERY_LENGTH,
                })
              : t('members.noResults')
          }
          renderOption={user => (
            <Box>
              <Typography variant="body2">{user.name || user.id}</Typography>
              {user.email && (
                <Typography variant="caption" className={classes.secondary}>
                  {user.email}
                </Typography>
              )}
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={t('members.searchLabel')}
              placeholder={t('members.searchPlaceholder')}
              variant="outlined"
              margin="dense"
            />
          )}
        />
        <Box mt={2}>
          <TextField
            select
            fullWidth
            label={t('table.role')}
            value={role}
            onChange={event => setRole(event.target.value as ProjectRole)}
            variant="outlined"
            margin="dense"
          >
            {ASSIGNABLE_PROJECT_ROLES.map(option => (
              <MenuItem key={option} value={option}>
                {t(`roles.${option}`)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {errorKind && (
          <Typography variant="body2" className={classes.caution}>
            {t(`members.errors.${errorKind}`)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!selected || submitting}
          onClick={submit}
        >
          {submitting ? t('members.adding') : t('actions.addMember')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
